/* ============================================
   ROOM DESIGNER — Equipment Module
   Asset file: custom-room-designer-equipment.js

   Handles equipment asset loading, pre-rasterized
   bitmap rendering (normal/bold/silhouette/stroke),
   lazy canvas building, theme rebuild on dark mode
   toggle, and equipment placement on canvas.
   Loaded before custom-room-designer.js via <script defer>.
   Instantiated by the RoomDesigner constructor.
   ============================================ */

class RoomDesignerEquipment {
  constructor(rd) {
    this.rd = rd;
    this._installMethods();
  }

  _installMethods() {
    const rd = this.rd;
    // Bind each method onto the RD instance so existing call sites
    // (this.methodName(...)) keep working unchanged.
    rd._loadEquipmentImages = this._loadEquipmentImages.bind(rd);
    rd._populateEquipmentFromArray = this._populateEquipmentFromArray.bind(rd);
    rd._ensureEquipmentLoaded = this._ensureEquipmentLoaded.bind(rd);
    rd._buildEquipmentNormal = this._buildEquipmentNormal.bind(rd);
    rd._ensureEquipmentCanvas = this._ensureEquipmentCanvas.bind(rd);
    rd._buildSilhouetteSync = this._buildSilhouetteSync.bind(rd);
    rd._rebuildEquipmentTheme = this._rebuildEquipmentTheme.bind(rd);
    rd._buildUpholsteryCanvas = this._buildUpholsteryCanvas.bind(rd);
    rd._buildRubberCanvas = this._buildRubberCanvas.bind(rd);
    rd._buildChromeCanvas = this._buildChromeCanvas.bind(rd);
    rd._buildWoodCanvas = this._buildWoodCanvas.bind(rd);
    rd._dilateForUpholstery = this._dilateForUpholstery.bind(rd);
    rd._getColoredCanvas = this._getColoredCanvas.bind(rd);
    rd._buildGlossCanvas = this._buildGlossCanvas.bind(rd);
    rd._buildSparkleCanvas = this._buildSparkleCanvas.bind(rd);
    rd._buildGrainCanvas = this._buildGrainCanvas.bind(rd);
    rd._ensurePatternImage = this._ensurePatternImage.bind(rd);
    rd._getPatternSource = this._getPatternSource.bind(rd);
    rd._getPatternedCanvas = this._getPatternedCanvas.bind(rd);
    rd._getPatternedPcCanvas = this._getPatternedPcCanvas.bind(rd);
    rd._invalidateBrandColorCaches = this._invalidateBrandColorCaches.bind(rd);
    rd._buildCrinkleUphCanvas = this._buildCrinkleUphCanvas.bind(rd);
    rd._buildCheckerChromeCanvas = this._buildCheckerChromeCanvas.bind(rd);
    rd._buildUphEdgeHighlight = this._buildUphEdgeHighlight.bind(rd);
    rd._loadTextureOverlays = this._loadTextureOverlays.bind(rd);
    rd.placeEquipment = this.placeEquipment.bind(rd);
    rd._resetQuoteNudgeTimer = this._resetQuoteNudgeTimer.bind(rd);
    rd._computeCardinalEquipSnap = this._computeCardinalEquipSnap.bind(rd);
    rd._equipRenderTimer = null; // coalescing timer for equipment load re-renders
  }

  // ── Methods ──

  /**
   * Load equipment catalog. Checks for inline Liquid data first (backward compat),
   * then falls back to fetching the static JSON asset.
   */
  _loadEquipmentImages() {
    // Backward compat: if Liquid loop already populated inline data, use it directly
    if (window.__rdEquipmentAssets && window.__rdEquipmentAssets.length > 0) {
      this._populateEquipmentFromArray(window.__rdEquipmentAssets);
      return;
    }
    // Async fetch from static JSON asset
    const url = window.__rdEquipmentAssetsUrl;
    if (!url) return;
    this._equipmentLoading = true;
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`Equipment JSON fetch failed: ${r.status}`);
        return r.json();
      })
      .then(data => {
        const products = data.products || data;
        this._populateEquipmentFromArray(Array.isArray(products) ? products : []);
        // Portal extras: merge supplemental catalog if available
        const extrasUrl = window.__rdEquipmentPortalExtrasUrl;
        if (extrasUrl) {
          return fetch(extrasUrl)
            .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
            .then(ed => {
              const extras = ed.products || ed;
              if (Array.isArray(extras) && extras.length > 0) {
                for (const item of extras) item.portalOnly = true;
                this._populateEquipmentFromArray(extras);
              }
            })
            .catch(err => console.warn('Portal extras load failed (non-fatal):', err));
        }
      })
      .then(() => {
        this._equipmentLoading = false;
        const overlay = window.roomDesignerOverlay;
        if (overlay && overlay.currentStep === 'equipment') {
          overlay._buildEquipmentPanelContent();
        }
        if (overlay && overlay._hideLoadingOverlay) {
          overlay._hideLoadingOverlay();
          this.render();
        }
        if (typeof this.updateMeasurements === 'function') this.updateMeasurements();
        // Pre-load SVGs for any equipment already placed on the canvas (auto-save restore).
        // Stagger loads so the main thread can breathe between pieces.
        if (this.objects && this.objects.length > 0) {
          const placedIds = [...new Set(
            this.objects.filter(o => o.type === 'equipment' && o.equipmentId).map(o => o.equipmentId)
          )];
          let i = 0;
          const loadNext = () => {
            if (i < placedIds.length) {
              this._ensureEquipmentLoaded(placedIds[i++]);
              setTimeout(loadNext, 0);
            }
          };
          loadNext();
        }
      })
      .catch(err => {
        console.error('Failed to load equipment catalog:', err);
        this._equipmentLoading = false;
      });
  }

  /**
   * Populate _equipmentImages from an array of product objects.
   * Shared by both the inline Liquid path and the async fetch path.
   */
  _populateEquipmentFromArray(assets) {
    for (const a of assets) {
      if (!a.id || !a.svgUrl || !a.widthIn || !a.depthIn) continue;
      this._equipmentImages[a.id] = {
        img: null,
        normalCanvas: null,
        boldCanvas: null,
        silCanvas: null,
        strokeCanvas: null,
        outlineCanvas: null,
        _themeDark: null,
        svgUrl: a.svgUrl,
        title: a.title || a.id,
        sku: a.sku || '',
        widthIn: a.widthIn,
        depthIn: a.depthIn,
        productType: a.productType || '',
        vendor: a.vendor || '',
        url: a.url || '',
        imageUrl: a.imageUrl || null,
        cw: 0,
        ch: 0,
        loaded: false,
        _loading: false,
        _callbacks: [],
        // Powder coat (metal frame) — true by default; all Legend equipment has metal
        hasPowderCoat: true,
        // Upholstery recoloring support
        hasUpholstery: false,
        _upholsteryMarkers: null,
        upholsteryCanvas: null,
        _coloredSilCanvas: null,
        _powderCoatCachedColor: null,
        _coloredUphCanvas: null,
        _upholsteryCachedColor: null,
        // Rubber region support (always-black areas like platform surfaces)
        hasRubber: false,
        _rubberMarkers: null,
        rubberCanvas: null,
        _coloredRubberCanvas: null,
        _rubberCachedColor: null,
        // Chrome/silver region support (always-silver areas like diamond plate)
        hasChrome: false,
        _chromeMarkers: null,
        chromeCanvas: null,
        _coloredChromeCanvas: null,
        _chromeCachedColor: null,
        _chromeGlossCanvas: null,
        // Wood region support (always-oak areas like platforms/handles)
        hasWood: false,
        _woodMarkers: null,
        woodCanvas: null,
        _coloredWoodCanvas: null,
        _woodCachedColor: null,
        // Tags for search filtering
        tags: Array.isArray(a.tags) ? a.tags : [],
        portalOnly: !!a.portalOnly
      };
    }
  }

  /**
   * Lazy-load SVG image on demand. Calls callback(entry) when ready.
   * If already loaded, calls callback immediately.
   * If already loading, queues callback.
   */
  _ensureEquipmentLoaded(id, callback) {
    const entry = this._equipmentImages[id];
    if (!entry) { if (callback) callback(null); return; }
    if (entry.loaded) { if (callback) callback(entry); return; }
    if (entry._loading) {
      if (callback) entry._callbacks.push(callback);
      return;
    }
    entry._loading = true;
    if (callback) entry._callbacks.push(callback);
    // Fetch SVG text in parallel with Image load.
    // Fetch extracts marker positions/radii; Image loads from original URL.
    // Magenta pixels are stripped at the pixel level after rasterization.
    const markerPromise = fetch(entry.svgUrl).then(r => r.text()).then(svgText => {
      let vbW = 0, vbH = 0;
      const vbMatch = svgText.match(/viewBox=["']([^"']+)["']/);
      if (vbMatch) {
        const parts = vbMatch[1].trim().split(/[\s,]+/);
        if (parts.length >= 4) { vbW = parseFloat(parts[2]); vbH = parseFloat(parts[3]); }
      }
      const markerCircleRe = /<circle[^>]*fill=["']#FF00FF["'][^>]*\/?>/gi;
      const svgMarkers = [];
      let match;
      while ((match = markerCircleRe.exec(svgText)) !== null) {
        const tag = match[0];
        const cxM = tag.match(/cx=["']([^"']+)["']/);
        const cyM = tag.match(/cy=["']([^"']+)["']/);
        if (cxM && cyM) {
          const gCloseIdx = svgText.lastIndexOf('</g>');
          const isOutside = gCloseIdx >= 0 && match.index > gCloseIdx;
          const rM = tag.match(/\br=["']([^"']+)["']/);
          svgMarkers.push({
            svgCx: parseFloat(cxM[1]), svgCy: parseFloat(cyM[1]),
            svgR: rM ? parseFloat(rM[1]) : 0, isOutside
          });
        }
      }
      // Rubber region markers: lime green (#00FF00) circles
      const rubberCircleRe = /<circle[^>]*fill=["']#00FF00["'][^>]*\/?>/gi;
      const rubberSvgMarkers = [];
      while ((match = rubberCircleRe.exec(svgText)) !== null) {
        const tag = match[0];
        const cxM = tag.match(/cx=["']([^"']+)["']/);
        const cyM = tag.match(/cy=["']([^"']+)["']/);
        if (cxM && cyM) {
          const gCloseIdx = svgText.lastIndexOf('</g>');
          const isOutside = gCloseIdx >= 0 && match.index > gCloseIdx;
          const rM = tag.match(/\br=["']([^"']+)["']/);
          rubberSvgMarkers.push({
            svgCx: parseFloat(cxM[1]), svgCy: parseFloat(cyM[1]),
            svgR: rM ? parseFloat(rM[1]) : 0, isOutside
          });
        }
      }
      // Chrome/silver region markers: cyan (#00FFFF) circles
      const chromeCircleRe = /<circle[^>]*fill=["']#00FFFF["'][^>]*\/?>/gi;
      const chromeSvgMarkers = [];
      while ((match = chromeCircleRe.exec(svgText)) !== null) {
        const tag = match[0];
        const cxM = tag.match(/cx=["']([^"']+)["']/);
        const cyM = tag.match(/cy=["']([^"']+)["']/);
        if (cxM && cyM) {
          const gCloseIdx = svgText.lastIndexOf('</g>');
          const isOutside = gCloseIdx >= 0 && match.index > gCloseIdx;
          const rM = tag.match(/\br=["']([^"']+)["']/);
          chromeSvgMarkers.push({
            svgCx: parseFloat(cxM[1]), svgCy: parseFloat(cyM[1]),
            svgR: rM ? parseFloat(rM[1]) : 0, isOutside
          });
        }
      }
      // Wood region markers: blue (#0000FF) circles
      const woodCircleRe = /<circle[^>]*fill=["']#0000FF["'][^>]*\/?>/gi;
      const woodSvgMarkers = [];
      while ((match = woodCircleRe.exec(svgText)) !== null) {
        const tag = match[0];
        const cxM = tag.match(/cx=["']([^"']+)["']/);
        const cyM = tag.match(/cy=["']([^"']+)["']/);
        if (cxM && cyM) {
          const gCloseIdx = svgText.lastIndexOf('</g>');
          const isOutside = gCloseIdx >= 0 && match.index > gCloseIdx;
          const rM = tag.match(/\br=["']([^"']+)["']/);
          woodSvgMarkers.push({
            svgCx: parseFloat(cxM[1]), svgCy: parseFloat(cyM[1]),
            svgR: rM ? parseFloat(rM[1]) : 0, isOutside
          });
        }
      }
      return { svgMarkers, rubberSvgMarkers, chromeSvgMarkers, woodSvgMarkers, vbW, vbH };
    }).catch(() => null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      markerPromise.then(markerData => {
        const wIn = entry.widthIn || 48, dIn = entry.depthIn || 48;
        const aspect = img.naturalWidth / img.naturalHeight;
        const longestIn = Math.max(wIn, dIn);
        const shortestIn = Math.min(wIn, dIn);
        const targetPxPerIn = 16;
        const minShortPx = 384;
        let maxDim = Math.round(longestIn * targetPxPerIn);
        const shortPx = Math.round(shortestIn / longestIn * maxDim);
        if (shortPx < minShortPx) {
          maxDim = Math.round(minShortPx * longestIn / shortestIn);
        }
        maxDim = Math.max(384, Math.min(1024, maxDim));
        entry.cw = aspect >= 1 ? maxDim : Math.round(maxDim * aspect);
        entry.ch = aspect >= 1 ? Math.round(maxDim / aspect) : maxDim;
        const cleanC = document.createElement('canvas');
        cleanC.width = entry.cw; cleanC.height = entry.ch;
        const cleanCtx = cleanC.getContext('2d');
        cleanCtx.drawImage(img, 0, 0, entry.cw, entry.ch);
        const imgData = cleanCtx.getImageData(0, 0, entry.cw, entry.ch);
        const d = imgData.data;
        // Strip white AND magenta pixels, compute tight content bounds
        let tMinX = entry.cw, tMinY = entry.ch, tMaxX = 0, tMaxY = 0;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i] >= 230 && d[i + 1] >= 230 && d[i + 2] >= 230) {
            d[i + 3] = 0; // white/near-white
          } else if (d[i] > 150 && d[i + 1] < 120 && d[i + 2] > 150 && d[i + 3] > 10) {
            d[i + 3] = 0; // magenta and anti-aliased magenta edge pixels
          } else if (d[i] < 120 && d[i + 1] > 150 && d[i + 2] < 120 && d[i + 3] > 10) {
            d[i + 3] = 0; // lime green and anti-aliased lime green edge pixels
          } else if (d[i] < 120 && d[i + 1] > 150 && d[i + 2] > 150 && d[i + 3] > 10) {
            d[i + 3] = 0; // cyan and anti-aliased cyan edge pixels
          } else if (d[i] < 80 && d[i + 1] < 80 && d[i + 2] > 150 && d[i + 3] > 10) {
            d[i + 3] = 0; // blue and anti-aliased blue edge pixels
          } else if (d[i + 3] > 10) {
            const px = (i / 4) % entry.cw, py = Math.floor((i / 4) / entry.cw);
            if (px < tMinX) tMinX = px;
            if (px > tMaxX) tMaxX = px;
            if (py < tMinY) tMinY = py;
            if (py > tMaxY) tMaxY = py;
          }
        }
        // Position-based wipe: erase generous circle at each marker to catch
        // any remaining artifacts (anti-aliased blends with line art underneath).
        if (markerData && markerData.svgMarkers.length > 0 && markerData.vbW > 0 && markerData.vbH > 0) {
          const { svgMarkers, vbW, vbH } = markerData;
          const markers = [];
          for (const m of svgMarkers) {
            const pixX = Math.round(m.svgCx / vbW * entry.cw);
            let pixY;
            if (m.isOutside) {
              pixY = Math.round(m.svgCy / vbH * entry.ch);
            } else {
              pixY = Math.round((vbH - m.svgCy) / vbH * entry.ch);
            }
            const pixRRaw = Math.ceil(Math.max(m.svgR / vbW * entry.cw, m.svgR / vbH * entry.ch));
            const pixR = pixRRaw + Math.min(4, Math.max(2, Math.round(pixRRaw * 0.3))); // proportional wipe: +2..4px
            const pixR2 = pixR * pixR;
            const xMin = Math.max(0, pixX - pixR);
            const xMax = Math.min(entry.cw - 1, pixX + pixR);
            const yMin = Math.max(0, pixY - pixR);
            const yMax = Math.min(entry.ch - 1, pixY + pixR);
            for (let py = yMin; py <= yMax; py++) {
              for (let px = xMin; px <= xMax; px++) {
                if ((px - pixX) * (px - pixX) + (py - pixY) * (py - pixY) <= pixR2) {
                  d[(py * entry.cw + px) * 4 + 3] = 0;
                }
              }
            }
            markers.push({ x: pixX, y: pixY, r: pixR, rRaw: pixRRaw });
          }
          entry._upholsteryMarkers = markers;
          entry.hasUpholstery = true;
        }
        // Rubber region position-based wipe: same generous-circle erase for lime green markers
        if (markerData && markerData.rubberSvgMarkers && markerData.rubberSvgMarkers.length > 0 && markerData.vbW > 0 && markerData.vbH > 0) {
          const { rubberSvgMarkers, vbW, vbH } = markerData;
          const rubberMarkers = [];
          for (const m of rubberSvgMarkers) {
            const pixX = Math.round(m.svgCx / vbW * entry.cw);
            let pixY;
            if (m.isOutside) {
              pixY = Math.round(m.svgCy / vbH * entry.ch);
            } else {
              pixY = Math.round((vbH - m.svgCy) / vbH * entry.ch);
            }
            const pixRRaw = Math.ceil(Math.max(m.svgR / vbW * entry.cw, m.svgR / vbH * entry.ch));
            const pixR = pixRRaw + Math.min(4, Math.max(2, Math.round(pixRRaw * 0.3)));
            const pixR2 = pixR * pixR;
            const xMin = Math.max(0, pixX - pixR);
            const xMax = Math.min(entry.cw - 1, pixX + pixR);
            const yMin = Math.max(0, pixY - pixR);
            const yMax = Math.min(entry.ch - 1, pixY + pixR);
            for (let py = yMin; py <= yMax; py++) {
              for (let px = xMin; px <= xMax; px++) {
                if ((px - pixX) * (px - pixX) + (py - pixY) * (py - pixY) <= pixR2) {
                  d[(py * entry.cw + px) * 4 + 3] = 0;
                }
              }
            }
            rubberMarkers.push({ x: pixX, y: pixY, r: pixR, rRaw: pixRRaw });
          }
          entry._rubberMarkers = rubberMarkers;
          entry.hasRubber = true;
        }
        // Chrome/silver region position-based wipe: same generous-circle erase for cyan markers
        if (markerData && markerData.chromeSvgMarkers && markerData.chromeSvgMarkers.length > 0 && markerData.vbW > 0 && markerData.vbH > 0) {
          const { chromeSvgMarkers, vbW, vbH } = markerData;
          const chromeMarkers = [];
          for (const m of chromeSvgMarkers) {
            const pixX = Math.round(m.svgCx / vbW * entry.cw);
            let pixY;
            if (m.isOutside) {
              pixY = Math.round(m.svgCy / vbH * entry.ch);
            } else {
              pixY = Math.round((vbH - m.svgCy) / vbH * entry.ch);
            }
            const pixRRaw = Math.ceil(Math.max(m.svgR / vbW * entry.cw, m.svgR / vbH * entry.ch));
            const pixR = pixRRaw + Math.min(4, Math.max(2, Math.round(pixRRaw * 0.3)));
            const pixR2 = pixR * pixR;
            const xMin = Math.max(0, pixX - pixR);
            const xMax = Math.min(entry.cw - 1, pixX + pixR);
            const yMin = Math.max(0, pixY - pixR);
            const yMax = Math.min(entry.ch - 1, pixY + pixR);
            for (let py = yMin; py <= yMax; py++) {
              for (let px = xMin; px <= xMax; px++) {
                if ((px - pixX) * (px - pixX) + (py - pixY) * (py - pixY) <= pixR2) {
                  d[(py * entry.cw + px) * 4 + 3] = 0;
                }
              }
            }
            chromeMarkers.push({ x: pixX, y: pixY, r: pixR, rRaw: pixRRaw });
          }
          entry._chromeMarkers = chromeMarkers;
          entry.hasChrome = true;
        }
        // Wood region position-based wipe: same generous-circle erase for blue markers
        if (markerData && markerData.woodSvgMarkers && markerData.woodSvgMarkers.length > 0 && markerData.vbW > 0 && markerData.vbH > 0) {
          const { woodSvgMarkers, vbW, vbH } = markerData;
          const woodMarkers = [];
          for (const m of woodSvgMarkers) {
            const pixX = Math.round(m.svgCx / vbW * entry.cw);
            let pixY;
            if (m.isOutside) {
              pixY = Math.round(m.svgCy / vbH * entry.ch);
            } else {
              pixY = Math.round((vbH - m.svgCy) / vbH * entry.ch);
            }
            const pixRRaw = Math.ceil(Math.max(m.svgR / vbW * entry.cw, m.svgR / vbH * entry.ch));
            const pixR = pixRRaw + Math.min(4, Math.max(2, Math.round(pixRRaw * 0.3)));
            const pixR2 = pixR * pixR;
            const xMin = Math.max(0, pixX - pixR);
            const xMax = Math.min(entry.cw - 1, pixX + pixR);
            const yMin = Math.max(0, pixY - pixR);
            const yMax = Math.min(entry.ch - 1, pixY + pixR);
            for (let py = yMin; py <= yMax; py++) {
              for (let px = xMin; px <= xMax; px++) {
                if ((px - pixX) * (px - pixX) + (py - pixY) * (py - pixY) <= pixR2) {
                  d[(py * entry.cw + px) * 4 + 3] = 0;
                }
              }
            }
            woodMarkers.push({ x: pixX, y: pixY, r: pixR, rRaw: pixRRaw });
          }
          entry._woodMarkers = woodMarkers;
          entry.hasWood = true;
        }
        // Check fp:no-frame tag — products tagged in Shopify as having no metal frame
        if (entry.tags && entry.tags.some(t => t === 'fp:no-frame')) entry.hasPowderCoat = false;
        cleanCtx.putImageData(imgData, 0, 0);
        // Tight dimensions + offset (visual content only, no SVG padding)
        if (tMaxX > tMinX && tMaxY > tMinY) {
          entry.tightWidthIn = wIn * ((tMaxX - tMinX + 1) / entry.cw);
          entry.tightDepthIn = dIn * ((tMaxY - tMinY + 1) / entry.ch);
          entry.tightOffsetXIn = wIn * (((tMinX + tMaxX) / 2 - entry.cw / 2 + 0.5) / entry.cw);
          entry.tightOffsetYIn = dIn * (((tMinY + tMaxY) / 2 - entry.ch / 2 + 0.5) / entry.ch);
        } else {
          entry.tightWidthIn = wIn;
          entry.tightDepthIn = dIn;
          entry.tightOffsetXIn = 0;
          entry.tightOffsetYIn = 0;
        }
        entry.img = cleanC;
        entry.normalCanvas = this._buildEquipmentNormal(entry);
        entry._themeDark = this.darkMode;
        entry.loaded = true;
        entry._loading = false;
        const cbs = entry._callbacks.splice(0);
        for (const cb of cbs) cb(entry);
        // Coalesce multiple equipment loads into one render (200ms window)
        clearTimeout(this._equipRenderTimer);
        this._equipRenderTimer = setTimeout(() => { this._equipRenderTimer = null; this.render(); }, 200);
      });
    };
    img.onerror = () => {
      console.warn('Failed to load equipment SVG:', id, entry.svgUrl);
      entry.loaded = true;  // Mark loaded so asset poll doesn't wait forever
      entry._loading = false;
      entry._failed = true;
      const cbs = entry._callbacks.splice(0);
      for (const cb of cbs) cb(null);
    };
    img.src = entry.svgUrl;
  }

  // Build the normal-detail bitmap canvas for one theme
  _buildEquipmentNormal(entry) {
    const { img, cw, ch } = entry;
    const c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, cw, ch);
    // Line art stays original black in both modes — no inversion.
    return c;
  }

  // Lazy-build bold/silhouette/stroke canvases on first render demand
  _ensureEquipmentCanvas(entry, which) {
    // Theme mismatch — clear only color caches (geometry masks are theme-independent)
    if (entry._themeDark !== this.darkMode) {
      entry._coloredSilCanvas = null;
      entry._powderCoatCachedColor = null;
      entry._coloredUphCanvas = null;
      entry._upholsteryCachedColor = null;
      entry._coloredRubberCanvas = null;
      entry._rubberCachedColor = null;
      entry._coloredChromeCanvas = null;
      entry._chromeCachedColor = null;
      entry._chromeGlossCanvas = null;
      entry._coloredWoodCanvas = null;
      entry._woodCachedColor = null;
      entry._patternedUphCanvas = null;
      entry._patternedUphCacheKey = null;
      entry._themeDark = this.darkMode;
    }
    if (which === 'normal') return entry.normalCanvas;
    if (which === 'bold') {
      if (!entry.boldCanvas) {
        const { cw, ch } = entry;
        const boldSpread = 2;
        const offsets = [[-1,0],[1,0],[0,-1],[0,1],[-1,-1],[1,-1],[-1,1],[1,1]];
        const bc = document.createElement('canvas');
        bc.width = cw; bc.height = ch;
        const bCtx = bc.getContext('2d');
        for (const [ox, oy] of offsets) {
          bCtx.drawImage(entry.normalCanvas, ox * boldSpread, oy * boldSpread);
        }
        bCtx.drawImage(entry.normalCanvas, 0, 0);
        entry.boldCanvas = bc;
      }
      return entry.boldCanvas;
    }
    if (which === 'sil') {
      if (!entry.silCanvas) {
        // Defer heavy silhouette generation so the render can finish fast.
        // Returns null on first call; schedules build via setTimeout.
        // After building, triggers a re-render so colors appear progressively.
        if (!entry._silScheduled) {
          entry._silScheduled = true;
          setTimeout(() => {
            this._buildSilhouetteSync(entry);
            this.render();
          }, 0);
        }
        return null;
      }
      return entry.silCanvas;
    }
    if (which === 'stroke') {
      if (!entry.strokeCanvas) {
        // Reuse silhouette canvas as the mask shape
        const silC = this._ensureEquipmentCanvas(entry, 'sil');
        if (!silC) return null;
        const { cw, ch } = entry;
        // Create black version of silhouette for compositing
        const silMask = document.createElement('canvas');
        silMask.width = cw; silMask.height = ch;
        const smCtx = silMask.getContext('2d');
        smCtx.drawImage(silC, 0, 0);
        smCtx.globalCompositeOperation = 'source-in';
        smCtx.fillStyle = '#000';
        smCtx.fillRect(0, 0, cw, ch);
        smCtx.globalCompositeOperation = 'source-over';
        const strokeInset = 9; // thicker contour stroke
        const color = '#333333';
        const stC = document.createElement('canvas');
        stC.width = cw; stC.height = ch;
        const stCtx = stC.getContext('2d');
        stCtx.fillStyle = color;
        stCtx.fillRect(0, 0, cw, ch);
        stCtx.globalCompositeOperation = 'destination-in';
        stCtx.drawImage(silMask, 0, 0);
        stCtx.globalCompositeOperation = 'destination-out';
        stCtx.drawImage(silMask, strokeInset, strokeInset, cw - strokeInset * 2, ch - strokeInset * 2);
        stCtx.globalCompositeOperation = 'source-over';
        entry.strokeCanvas = stC;
      }
      return entry.strokeCanvas;
    }
    if (which === 'outline') {
      if (!entry.outlineCanvas) {
        const silC = this._ensureEquipmentCanvas(entry, 'sil');
        if (!silC) return null;
        const { cw, ch } = entry;
        const spread = 3; // outline width in bitmap pixels
        // Expand silhouette by stamping in 8 directions
        const expandC = document.createElement('canvas');
        expandC.width = cw; expandC.height = ch;
        const exCtx = expandC.getContext('2d');
        const offsets = [[-1,0],[1,0],[0,-1],[0,1],[-1,-1],[1,-1],[-1,1],[1,1]];
        for (const [ox, oy] of offsets) {
          exCtx.drawImage(silC, ox * spread, oy * spread);
        }
        exCtx.drawImage(silC, 0, 0);
        // Fill with outline color, mask to expanded shape, cut out original interior
        const olC = document.createElement('canvas');
        olC.width = cw; olC.height = ch;
        const olCtx = olC.getContext('2d');
        olCtx.fillStyle = '#333333';
        olCtx.fillRect(0, 0, cw, ch);
        olCtx.globalCompositeOperation = 'destination-in';
        olCtx.drawImage(expandC, 0, 0);
        olCtx.globalCompositeOperation = 'destination-out';
        olCtx.drawImage(silC, 0, 0);
        olCtx.globalCompositeOperation = 'source-over';
        entry.outlineCanvas = olC;
      }
      return entry.outlineCanvas;
    }
    return null;
  }

  // Build silhouette canvas synchronously for one equipment entry.
  // Extracted so it can be called from a deferred setTimeout in _ensureEquipmentCanvas.
  _buildSilhouetteSync(entry) {
    if (entry.silCanvas) return; // already built
    const { cw, ch } = entry;
    // --- High-res analysis pass ---
    // Render canvases stay at cw×ch (1024 cap) to limit GPU memory, but the
    // mask / exterior / hole-detection BFS runs at 2× resolution (~2048, the
    // original size where dilation values 3/8/1/2 were tuned and worked).
    // All analysis data is Uint8Arrays on JS heap (not GPU textures) plus one
    // temporary canvas — freed immediately after getImageData.
    const AW = cw * 2, AH = ch * 2;
    const tmpC = document.createElement('canvas');
    tmpC.width = AW; tmpC.height = AH;
    tmpC.getContext('2d').drawImage(entry.img, 0, 0, AW, AH);
    const srcData = tmpC.getContext('2d').getImageData(0, 0, AW, AH).data;
    // Free the high-res canvas immediately — we only need the pixel data
    tmpC.width = 1; tmpC.height = 1;
    // Binary mask: 1 = opaque pixel (equipment line), 0 = transparent
    const mask = new Uint8Array(AW * AH);
    for (let i = 0; i < AW * AH; i++) {
      mask[i] = srcData[i * 4 + 3] > 20 ? 1 : 0;
    }
    // Helper: two-pass separable box dilation using prefix sums — O(w×h)
    // regardless of radius (vs old O(w×h×r) inner loop approach).
    const _sepDilate = (src, r) => {
      const h = new Uint8Array(AW * AH);
      // Horizontal pass: for each row, prefix-sum to detect any set pixel in [x-r, x+r]
      for (let y = 0; y < AH; y++) {
        const rowOff = y * AW;
        let count = 0;
        // Initialize window [0, r]
        for (let x = 0; x <= r && x < AW; x++) count += src[rowOff + x];
        if (count) h[rowOff] = 1;
        for (let x = 1; x < AW; x++) {
          const addX = x + r, dropX = x - r - 1;
          if (addX < AW) count += src[rowOff + addX];
          if (dropX >= 0) count -= src[rowOff + dropX];
          if (count) h[rowOff + x] = 1;
        }
      }
      // Vertical pass: for each column, prefix-sum on h to produce final output
      const out = new Uint8Array(AW * AH);
      for (let x = 0; x < AW; x++) {
        let count = 0;
        for (let y = 0; y <= r && y < AH; y++) count += h[y * AW + x];
        if (count) out[x] = 1;
        for (let y = 1; y < AH; y++) {
          const addY = y + r, dropY = y - r - 1;
          if (addY < AH) count += h[addY * AW + x];
          if (dropY >= 0) count -= h[dropY * AW + x];
          if (count) out[y * AW + x] = 1;
        }
      }
      return out;
    };
    // --- Two-phase exterior flood fill (at 4× = ~2048px, original resolution) ---
    // All dilation values are the originals, tuned for this resolution.
    const dilated = _sepDilate(mask, 3);
    const hiExterior = new Uint8Array(AW * AH);
    const queue = new Int32Array(AW * AH);
    let head = 0, tail = 0;
    for (let x = 0; x < AW; x++) {
      if (!dilated[x] && !hiExterior[x]) { hiExterior[x] = 1; queue[tail++] = x; }
      const bot = (AH - 1) * AW + x;
      if (!dilated[bot] && !hiExterior[bot]) { hiExterior[bot] = 1; queue[tail++] = bot; }
    }
    for (let y = 1; y < AH - 1; y++) {
      const left = y * AW;
      if (!dilated[left] && !hiExterior[left]) { hiExterior[left] = 1; queue[tail++] = left; }
      const right = y * AW + AW - 1;
      if (!dilated[right] && !hiExterior[right]) { hiExterior[right] = 1; queue[tail++] = right; }
    }
    while (head < tail) {
      const idx = queue[head++];
      const x = idx % AW, y = (idx - x) / AW;
      if (y > 0) { const n = idx - AW; if (!hiExterior[n] && !dilated[n]) { hiExterior[n] = 1; queue[tail++] = n; } }
      if (y < AH - 1) { const n = idx + AW; if (!hiExterior[n] && !dilated[n]) { hiExterior[n] = 1; queue[tail++] = n; } }
      if (x > 0) { const n = idx - 1; if (!hiExterior[n] && !dilated[n]) { hiExterior[n] = 1; queue[tail++] = n; } }
      if (x < AW - 1) { const n = idx + 1; if (!hiExterior[n] && !dilated[n]) { hiExterior[n] = 1; queue[tail++] = n; } }
    }
    // Phase 2: raw mask barrier
    head = 0; tail = 0;
    for (let i = 0; i < AW * AH; i++) {
      if (hiExterior[i]) { queue[tail++] = i; }
    }
    while (head < tail) {
      const idx = queue[head++];
      const x = idx % AW, y = (idx - x) / AW;
      if (y > 0) { const n = idx - AW; if (!hiExterior[n] && !mask[n]) { hiExterior[n] = 1; queue[tail++] = n; } }
      if (y < AH - 1) { const n = idx + AW; if (!hiExterior[n] && !mask[n]) { hiExterior[n] = 1; queue[tail++] = n; } }
      if (x > 0) { const n = idx - 1; if (!hiExterior[n] && !mask[n]) { hiExterior[n] = 1; queue[tail++] = n; } }
      if (x < AW - 1) { const n = idx + 1; if (!hiExterior[n] && !mask[n]) { hiExterior[n] = 1; queue[tail++] = n; } }
    }
    // --- Interior hole detection (at 4× = ~2048px, original resolution) ---
    const dilWide = _sepDilate(mask, 8);
    const dilThin = _sepDilate(mask, 1);
    const HOLE_MIN_DIM = 15;
    const voidVisited = new Uint8Array(AW * AH);
    for (let sy = 0; sy < AH; sy++) {
      for (let sx = 0; sx < AW; sx++) {
        const si = sy * AW + sx;
        if (hiExterior[si] || dilWide[si] || voidVisited[si]) continue;
        const component = [si];
        voidVisited[si] = 1;
        let bfsH = 0;
        let minX = sx, maxX = sx, minY = sy, maxY = sy;
        while (bfsH < component.length) {
          const ci = component[bfsH++];
          const cx = ci % AW, cy = (ci - cx) / AW;
          if (cx < minX) minX = cx; if (cx > maxX) maxX = cx;
          if (cy < minY) minY = cy; if (cy > maxY) maxY = cy;
          if (cy > 0)      { const n = ci - AW; if (!voidVisited[n] && !hiExterior[n] && !dilWide[n]) { voidVisited[n] = 1; component.push(n); } }
          if (cy < AH - 1) { const n = ci + AW; if (!voidVisited[n] && !hiExterior[n] && !dilWide[n]) { voidVisited[n] = 1; component.push(n); } }
          if (cx > 0)      { const n = ci - 1;  if (!voidVisited[n] && !hiExterior[n] && !dilWide[n]) { voidVisited[n] = 1; component.push(n); } }
          if (cx < AW - 1) { const n = ci + 1;  if (!voidVisited[n] && !hiExterior[n] && !dilWide[n]) { voidVisited[n] = 1; component.push(n); } }
        }
        const bboxW = maxX - minX + 1;
        const bboxH = maxY - minY + 1;
        if (Math.min(bboxW, bboxH) < HOLE_MIN_DIM) continue;
        const probeVisited = new Uint8Array(AW * AH);
        const probeQueue = [];
        let isHole = false;
        for (const pi of component) {
          probeVisited[pi] = 1;
          probeQueue.push(pi);
        }
        let pH = 0;
        while (pH < probeQueue.length) {
          const ci = probeQueue[pH++];
          const cx = ci % AW, cy = (ci - cx) / AW;
          const nb = [];
          if (cy > 0) nb.push(ci - AW);
          if (cy < AH - 1) nb.push(ci + AW);
          if (cx > 0) nb.push(ci - 1);
          if (cx < AW - 1) nb.push(ci + 1);
          for (const ni of nb) {
            if (hiExterior[ni]) { isHole = true; break; }
            if (!probeVisited[ni] && !dilThin[ni]) {
              probeVisited[ni] = 1;
              probeQueue.push(ni);
            }
          }
          if (isHole) break;
        }
        if (!isHole) continue;
        for (const pi of component) {
          if (!hiExterior[pi]) hiExterior[pi] = 1;
        }
        let expH = 0;
        const expQueue = component.slice();
        while (expH < expQueue.length) {
          const ei = expQueue[expH++];
          const ex = ei % AW, ey = (ei - ex) / AW;
          if (ey > 0)      { const n = ei - AW; if (!hiExterior[n] && !mask[n]) { hiExterior[n] = 1; expQueue.push(n); } }
          if (ey < AH - 1) { const n = ei + AW; if (!hiExterior[n] && !mask[n]) { hiExterior[n] = 1; expQueue.push(n); } }
          if (ex > 0)      { const n = ei - 1;  if (!hiExterior[n] && !mask[n]) { hiExterior[n] = 1; expQueue.push(n); } }
          if (ex < AW - 1) { const n = ei + 1;  if (!hiExterior[n] && !mask[n]) { hiExterior[n] = 1; expQueue.push(n); } }
        }
      }
    }
    // Erode interior by 2px (original value for ~2048px analysis)
    const extDilated = _sepDilate(hiExterior, 2);
    for (let i = 0; i < AW * AH; i++) {
      if (extDilated[i]) hiExterior[i] = 1;
    }
    // --- Downscale exterior mask from 2× back to render resolution ---
    // A 2×2 block is exterior if ANY of its 4 hi-res pixels is exterior.
    const exterior = new Uint8Array(cw * ch);
    for (let y = 0; y < ch; y++) {
      for (let x = 0; x < cw; x++) {
        const hx = x * 2, hy = y * 2;
        if (hiExterior[hy * AW + hx] || hiExterior[hy * AW + hx + 1] ||
            hiExterior[(hy + 1) * AW + hx] || hiExterior[(hy + 1) * AW + hx + 1]) {
          exterior[y * cw + x] = 1;
        }
      }
    }
    entry._exterior = exterior;
    // Silhouette = everything not exterior
    const color = this.darkMode ? '#e8e8e8' : '#2a3a52';
    const sc = document.createElement('canvas');
    sc.width = cw; sc.height = ch;
    const ctx2 = sc.getContext('2d');
    ctx2.fillStyle = color;
    for (let y = 0; y < ch; y++) {
      let runStart = -1;
      for (let x = 0; x <= cw; x++) {
        const idx = y * cw + x;
        const inside = x < cw && !exterior[idx];
        if (inside && runStart === -1) runStart = x;
        if (!inside && runStart !== -1) {
          ctx2.fillRect(runStart, y, x - runStart, 1);
          runStart = -1;
        }
      }
    }
    entry.silCanvas = sc;
    if (entry.hasUpholstery && !entry.upholsteryCanvas) this._buildUpholsteryCanvas(entry);
    if (entry.hasRubber && !entry.rubberCanvas) this._buildRubberCanvas(entry);
    if (entry.hasChrome && !entry.chromeCanvas) this._buildChromeCanvas(entry);
    if (entry.hasWood && !entry.woodCanvas) this._buildWoodCanvas(entry);
    // Free source canvas — normalCanvas has the same pixel data and is used for
    // rendering + gloss builds. This saves ~4MB per piece of GPU memory.
    entry.img = null;
  }

  // Called on dark mode toggle — clear only theme-dependent color caches.
  // Geometry masks (sil, bold, stroke, outline, upholstery, rubber, chrome,
  // wood, gloss, grain, exterior) are theme-independent and kept intact.
  _rebuildEquipmentTheme() {
    for (const id in this._equipmentImages) {
      const entry = this._equipmentImages[id];
      if (!entry.loaded) continue;
      // Color caches depend on theme (default powder coat differs light/dark)
      entry._coloredSilCanvas = null;
      entry._powderCoatCachedColor = null;
      entry._coloredUphCanvas = null;
      entry._upholsteryCachedColor = null;
      entry._coloredRubberCanvas = null;
      entry._rubberCachedColor = null;
      entry._coloredChromeCanvas = null;
      entry._chromeCachedColor = null;
      entry._chromeGlossCanvas = null;
      entry._coloredWoodCanvas = null;
      entry._woodCachedColor = null;
      entry._patternedUphCanvas = null;
      entry._patternedUphCacheKey = null;
      entry._patternedPcCanvas = null;
      entry._patternedPcCacheKey = null;
      entry._themeDark = this.darkMode;
    }
  }

  /**
   * Build upholstery mask canvas via flood-fill from marker centers.
   * The mask is a white-alpha canvas where white = pad region.
   * Uses dilated line-art as a barrier to contain the fill within pad boundaries.
   */
  _buildUpholsteryCanvas(entry) {
    if (!entry._upholsteryMarkers || entry._upholsteryMarkers.length === 0) return;
    const { cw, ch } = entry;
    // Get raw pixel data from the clean source image (pre-inversion)
    const tmpC = document.createElement('canvas');
    tmpC.width = cw; tmpC.height = ch;
    tmpC.getContext('2d').drawImage(entry.img, 0, 0, cw, ch);
    const srcData = tmpC.getContext('2d').getImageData(0, 0, cw, ch).data;
    // Build binary line-art mask (alpha > 20 = barrier)
    const barrier = new Uint8Array(cw * ch);
    for (let i = 0; i < cw * ch; i++) {
      barrier[i] = srcData[i * 4 + 3] > 20 ? 1 : 0;
    }
    // Dilate barrier 2px to seal small line-art gaps
    const dilated = this._dilateForUpholstery(barrier, cw, ch, 2);
    // BFS flood-fill from each marker center
    const filled = new Uint8Array(cw * ch);
    for (const marker of entry._upholsteryMarkers) {
      const startIdx = marker.y * cw + marker.x;
      if (startIdx < 0 || startIdx >= cw * ch) continue;
      if (dilated[startIdx] || filled[startIdx]) continue;
      const queue = [startIdx];
      filled[startIdx] = 1;
      let qH = 0;
      while (qH < queue.length) {
        const ci = queue[qH++];
        const cx = ci % cw, cy = (ci - cx) / cw;
        const nb = [];
        if (cy > 0) nb.push(ci - cw);
        if (cy < ch - 1) nb.push(ci + cw);
        if (cx > 0) nb.push(ci - 1);
        if (cx < cw - 1) nb.push(ci + 1);
        for (const ni of nb) {
          if (!filled[ni] && !dilated[ni]) {
            filled[ni] = 1;
            queue.push(ni);
          }
        }
      }
    }
    // Constrained expansion: BFS from fill boundary through non-barrier pixels.
    // Uses the silhouette exterior mask (if available) as hard boundary instead of
    // a fixed distance limit. This fills narrow tapered pad ends that the 2px-dilated
    // barrier seals off, without leaking outside the equipment body.
    {
      const ext = entry._exterior;
      const expQ = new Int32Array(cw * ch);
      let eH = 0, eT = 0;
      // Seed: non-filled, non-barrier 4-neighbors of filled pixels
      for (let i = 0; i < cw * ch; i++) {
        if (!filled[i]) continue;
        const x = i % cw, y = (i - x) / cw;
        if (x > 0)      { const n = i - 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (x < cw - 1) { const n = i + 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (y > 0)      { const n = i - cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (y < ch - 1) { const n = i + cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
      }
      while (eH < eT) {
        const ci = expQ[eH++];
        const cx = ci % cw, cy = (ci - cx) / cw;
        if (cx > 0)      { const n = ci - 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cx < cw - 1) { const n = ci + 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cy > 0)      { const n = ci - cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cy < ch - 1) { const n = ci + cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
      }
    }
    // Final 2px dilation: push fill edge slightly into stroke fringe so the
    // upholstery color underlaps the line art. Without this, anti-aliased stroke
    // edges (alpha 20-128) show the silhouette base color through. Constrained
    // to stay inside the equipment body (not exterior).
    {
      const dilFill = this._dilateForUpholstery(filled, cw, ch, 2);
      const ext = entry._exterior;
      for (let i = 0; i < cw * ch; i++) {
        if (dilFill[i] && !filled[i] && !(ext && ext[i])) filled[i] = 1;
      }
    }
    // Write filled region as white alpha mask
    const uphC = document.createElement('canvas');
    uphC.width = cw; uphC.height = ch;
    const uphCtx = uphC.getContext('2d');
    const uphData = uphCtx.createImageData(cw, ch);
    const ud = uphData.data;
    for (let i = 0; i < cw * ch; i++) {
      if (filled[i]) {
        ud[i * 4] = 255;
        ud[i * 4 + 1] = 255;
        ud[i * 4 + 2] = 255;
        ud[i * 4 + 3] = 255;
      }
    }
    uphCtx.putImageData(uphData, 0, 0);
    entry.upholsteryCanvas = uphC;
  }

  /**
   * Build rubber region mask canvas via flood-fill from rubber marker centers.
   * Identical approach to _buildUpholsteryCanvas but reads _rubberMarkers.
   * The mask is a white-alpha canvas where white = rubber region.
   */
  _buildRubberCanvas(entry) {
    if (!entry._rubberMarkers || entry._rubberMarkers.length === 0) return;
    const { cw, ch } = entry;
    const tmpC = document.createElement('canvas');
    tmpC.width = cw; tmpC.height = ch;
    tmpC.getContext('2d').drawImage(entry.img, 0, 0, cw, ch);
    const srcData = tmpC.getContext('2d').getImageData(0, 0, cw, ch).data;
    const barrier = new Uint8Array(cw * ch);
    for (let i = 0; i < cw * ch; i++) {
      barrier[i] = srcData[i * 4 + 3] > 20 ? 1 : 0;
    }
    const dilated = this._dilateForUpholstery(barrier, cw, ch, 2);
    const filled = new Uint8Array(cw * ch);
    for (const marker of entry._rubberMarkers) {
      const startIdx = marker.y * cw + marker.x;
      if (startIdx < 0 || startIdx >= cw * ch) continue;
      if (dilated[startIdx] || filled[startIdx]) continue;
      const queue = [startIdx];
      filled[startIdx] = 1;
      let qH = 0;
      while (qH < queue.length) {
        const ci = queue[qH++];
        const cx = ci % cw, cy = (ci - cx) / cw;
        const nb = [];
        if (cy > 0) nb.push(ci - cw);
        if (cy < ch - 1) nb.push(ci + cw);
        if (cx > 0) nb.push(ci - 1);
        if (cx < cw - 1) nb.push(ci + 1);
        for (const ni of nb) {
          if (!filled[ni] && !dilated[ni]) {
            filled[ni] = 1;
            queue.push(ni);
          }
        }
      }
    }
    // Constrained expansion: unlimited BFS with exterior mask constraint, same as upholstery
    {
      const ext = entry._exterior;
      const expQ = new Int32Array(cw * ch);
      let eH = 0, eT = 0;
      for (let i = 0; i < cw * ch; i++) {
        if (!filled[i]) continue;
        const x = i % cw, y = (i - x) / cw;
        if (x > 0)      { const n = i - 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (x < cw - 1) { const n = i + 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (y > 0)      { const n = i - cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (y < ch - 1) { const n = i + cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
      }
      while (eH < eT) {
        const ci = expQ[eH++];
        const cx = ci % cw, cy = (ci - cx) / cw;
        if (cx > 0)      { const n = ci - 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cx < cw - 1) { const n = ci + 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cy > 0)      { const n = ci - cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cy < ch - 1) { const n = ci + cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
      }
    }
    // Final 2px dilation into stroke fringe, constrained to equipment body
    {
      const dilFill = this._dilateForUpholstery(filled, cw, ch, 2);
      const ext = entry._exterior;
      for (let i = 0; i < cw * ch; i++) {
        if (dilFill[i] && !filled[i] && !(ext && ext[i])) filled[i] = 1;
      }
    }
    const rubC = document.createElement('canvas');
    rubC.width = cw; rubC.height = ch;
    const rubCtx = rubC.getContext('2d');
    const rubData = rubCtx.createImageData(cw, ch);
    const rd = rubData.data;
    for (let i = 0; i < cw * ch; i++) {
      if (filled[i]) {
        rd[i * 4] = 255;
        rd[i * 4 + 1] = 255;
        rd[i * 4 + 2] = 255;
        rd[i * 4 + 3] = 255;
      }
    }
    rubCtx.putImageData(rubData, 0, 0);
    entry.rubberCanvas = rubC;
  }

  /**
   * Build chrome/silver region mask canvas via flood-fill from chrome marker centers.
   * Identical approach to _buildRubberCanvas but reads _chromeMarkers.
   * The mask is a white-alpha canvas where white = chrome region.
   */
  _buildChromeCanvas(entry) {
    if (!entry._chromeMarkers || entry._chromeMarkers.length === 0) return;
    const { cw, ch } = entry;
    const tmpC = document.createElement('canvas');
    tmpC.width = cw; tmpC.height = ch;
    tmpC.getContext('2d').drawImage(entry.img, 0, 0, cw, ch);
    const srcData = tmpC.getContext('2d').getImageData(0, 0, cw, ch).data;
    const barrier = new Uint8Array(cw * ch);
    for (let i = 0; i < cw * ch; i++) {
      barrier[i] = srcData[i * 4 + 3] > 20 ? 1 : 0;
    }
    const dilated = this._dilateForUpholstery(barrier, cw, ch, 2);
    const filled = new Uint8Array(cw * ch);
    for (const marker of entry._chromeMarkers) {
      const startIdx = marker.y * cw + marker.x;
      if (startIdx < 0 || startIdx >= cw * ch) continue;
      if (dilated[startIdx] || filled[startIdx]) continue;
      const queue = [startIdx];
      filled[startIdx] = 1;
      let qH = 0;
      while (qH < queue.length) {
        const ci = queue[qH++];
        const cx = ci % cw, cy = (ci - cx) / cw;
        const nb = [];
        if (cy > 0) nb.push(ci - cw);
        if (cy < ch - 1) nb.push(ci + cw);
        if (cx > 0) nb.push(ci - 1);
        if (cx < cw - 1) nb.push(ci + 1);
        for (const ni of nb) {
          if (!filled[ni] && !dilated[ni]) {
            filled[ni] = 1;
            queue.push(ni);
          }
        }
      }
    }
    // Constrained expansion: unlimited BFS with exterior mask constraint
    {
      const ext = entry._exterior;
      const expQ = new Int32Array(cw * ch);
      let eH = 0, eT = 0;
      for (let i = 0; i < cw * ch; i++) {
        if (!filled[i]) continue;
        const x = i % cw, y = (i - x) / cw;
        if (x > 0)      { const n = i - 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (x < cw - 1) { const n = i + 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (y > 0)      { const n = i - cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (y < ch - 1) { const n = i + cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
      }
      while (eH < eT) {
        const ci = expQ[eH++];
        const cx = ci % cw, cy = (ci - cx) / cw;
        if (cx > 0)      { const n = ci - 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cx < cw - 1) { const n = ci + 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cy > 0)      { const n = ci - cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cy < ch - 1) { const n = ci + cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
      }
    }
    // Final 2px dilation into stroke fringe, constrained to equipment body
    {
      const dilFill = this._dilateForUpholstery(filled, cw, ch, 2);
      const ext = entry._exterior;
      for (let i = 0; i < cw * ch; i++) {
        if (dilFill[i] && !filled[i] && !(ext && ext[i])) filled[i] = 1;
      }
    }
    const chrC = document.createElement('canvas');
    chrC.width = cw; chrC.height = ch;
    const chrCtx = chrC.getContext('2d');
    const chrData = chrCtx.createImageData(cw, ch);
    const cd = chrData.data;
    for (let i = 0; i < cw * ch; i++) {
      if (filled[i]) {
        cd[i * 4] = 255;
        cd[i * 4 + 1] = 255;
        cd[i * 4 + 2] = 255;
        cd[i * 4 + 3] = 255;
      }
    }
    chrCtx.putImageData(chrData, 0, 0);
    entry.chromeCanvas = chrC;
  }

  /**
   * Build wood region mask canvas via flood-fill from wood marker centers.
   * Identical approach to _buildRubberCanvas but reads _woodMarkers.
   * The mask is a white-alpha canvas where white = wood region.
   */
  _buildWoodCanvas(entry) {
    if (!entry._woodMarkers || entry._woodMarkers.length === 0) return;
    const { cw, ch } = entry;
    const tmpC = document.createElement('canvas');
    tmpC.width = cw; tmpC.height = ch;
    tmpC.getContext('2d').drawImage(entry.img, 0, 0, cw, ch);
    const srcData = tmpC.getContext('2d').getImageData(0, 0, cw, ch).data;
    const barrier = new Uint8Array(cw * ch);
    for (let i = 0; i < cw * ch; i++) {
      barrier[i] = srcData[i * 4 + 3] > 20 ? 1 : 0;
    }
    const dilated = this._dilateForUpholstery(barrier, cw, ch, 2);
    const filled = new Uint8Array(cw * ch);
    for (const marker of entry._woodMarkers) {
      const startIdx = marker.y * cw + marker.x;
      if (startIdx < 0 || startIdx >= cw * ch) continue;
      if (dilated[startIdx] || filled[startIdx]) continue;
      const queue = [startIdx];
      filled[startIdx] = 1;
      let qH = 0;
      while (qH < queue.length) {
        const ci = queue[qH++];
        const cx = ci % cw, cy = (ci - cx) / cw;
        const nb = [];
        if (cy > 0) nb.push(ci - cw);
        if (cy < ch - 1) nb.push(ci + cw);
        if (cx > 0) nb.push(ci - 1);
        if (cx < cw - 1) nb.push(ci + 1);
        for (const ni of nb) {
          if (!filled[ni] && !dilated[ni]) {
            filled[ni] = 1;
            queue.push(ni);
          }
        }
      }
    }
    // Constrained expansion: unlimited BFS with exterior mask constraint
    {
      const ext = entry._exterior;
      const expQ = new Int32Array(cw * ch);
      let eH = 0, eT = 0;
      for (let i = 0; i < cw * ch; i++) {
        if (!filled[i]) continue;
        const x = i % cw, y = (i - x) / cw;
        if (x > 0)      { const n = i - 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (x < cw - 1) { const n = i + 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (y > 0)      { const n = i - cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (y < ch - 1) { const n = i + cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
      }
      while (eH < eT) {
        const ci = expQ[eH++];
        const cx = ci % cw, cy = (ci - cx) / cw;
        if (cx > 0)      { const n = ci - 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cx < cw - 1) { const n = ci + 1;  if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cy > 0)      { const n = ci - cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
        if (cy < ch - 1) { const n = ci + cw; if (!filled[n] && !barrier[n] && !(ext && ext[n])) { filled[n] = 1; expQ[eT++] = n; } }
      }
    }
    // Final 2px dilation into stroke fringe, constrained to equipment body
    {
      const dilFill = this._dilateForUpholstery(filled, cw, ch, 2);
      const ext = entry._exterior;
      for (let i = 0; i < cw * ch; i++) {
        if (dilFill[i] && !filled[i] && !(ext && ext[i])) filled[i] = 1;
      }
    }
    const wdC = document.createElement('canvas');
    wdC.width = cw; wdC.height = ch;
    const wdCtx = wdC.getContext('2d');
    const wdData = wdCtx.createImageData(cw, ch);
    const wd = wdData.data;
    for (let i = 0; i < cw * ch; i++) {
      if (filled[i]) {
        wd[i * 4] = 255;
        wd[i * 4 + 1] = 255;
        wd[i * 4 + 2] = 255;
        wd[i * 4 + 3] = 255;
      }
    }
    wdCtx.putImageData(wdData, 0, 0);
    entry.woodCanvas = wdC;
  }

  /**
   * Dilate a binary mask by radius pixels (separable box dilation).
   * Helper for _buildUpholsteryCanvas, _buildRubberCanvas, _buildChromeCanvas, and _buildWoodCanvas.
   */
  _dilateForUpholstery(mask, w, h, radius) {
    // Horizontal pass
    const horiz = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) {
        if (!mask[row + x]) continue;
        const x0 = Math.max(0, x - radius), x1 = Math.min(w - 1, x + radius);
        for (let xx = x0; xx <= x1; xx++) horiz[row + xx] = 1;
      }
    }
    // Vertical pass
    const out = new Uint8Array(w * h);
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) {
        if (!horiz[y * w + x]) continue;
        const y0 = Math.max(0, y - radius), y1 = Math.min(h - 1, y + radius);
        for (let yy = y0; yy <= y1; yy++) out[yy * w + x] = 1;
      }
    }
    return out;
  }

  /**
   * Get a recolored version of a source canvas using source-in compositing.
   * Caches the result on the entry to avoid rebuilding every frame.
   * @param {HTMLCanvasElement} source - Source canvas (silhouette or upholstery mask)
   * @param {string} color - Hex color string
   * @param {object} entry - Equipment entry for caching
   * @param {string} cacheProp - Property name for cached canvas (e.g. '_coloredSilCanvas')
   * @param {string} colorProp - Property name for cached color (e.g. '_powderCoatCachedColor')
   * @returns {HTMLCanvasElement}
   */
  /**
   * Build a per-panel gloss overlay canvas.
   * Segments the equipment into individual steel panels using the raw SVG
   * line art as flood-fill barriers, then applies a gradient to each panel
   * based on its own bounding box. Cached per entry; rebuilt on theme change.
   */
  _buildGlossCanvas(entry) {
    if (entry._glossCanvas) return entry._glossCanvas;
    const { cw, ch } = entry;
    if (!entry.normalCanvas || !cw || !ch) return null;
    const sil = entry.silCanvas || this._ensureEquipmentCanvas(entry, 'sil');
    if (!sil) return null;

    // Rasterize raw SVG for barrier detection (black strokes, theme-independent)
    const tmp = document.createElement('canvas');
    tmp.width = cw; tmp.height = ch;
    const tctx = tmp.getContext('2d');
    tctx.drawImage(entry.normalCanvas, 0, 0, cw, ch);
    const lineData = tctx.getImageData(0, 0, cw, ch).data;
    tctx.clearRect(0, 0, cw, ch);
    tctx.drawImage(sil, 0, 0);
    const silData = tctx.getImageData(0, 0, cw, ch).data;

    // Build barrier mask: line-art stroke OR outside silhouette
    const N = cw * ch;
    const barrier = new Uint8Array(N);
    for (let i = 0; i < N; i++) {
      barrier[i] = (lineData[i * 4 + 3] > 30 || silData[i * 4 + 3] < 30) ? 1 : 0;
    }
    // Dilate barriers 1px to close small gaps
    const dilated = new Uint8Array(barrier);
    for (let y = 0; y < ch; y++) {
      for (let x = 0; x < cw; x++) {
        if (barrier[y * cw + x]) continue;
        if ((x > 0 && barrier[y * cw + x - 1]) || (x < cw - 1 && barrier[y * cw + x + 1]) ||
            (y > 0 && barrier[(y - 1) * cw + x]) || (y < ch - 1 && barrier[(y + 1) * cw + x])) {
          dilated[y * cw + x] = 1;
        }
      }
    }

    // BFS flood-fill to label each enclosed panel
    const labels = new Int32Array(N);
    for (let i = 0; i < N; i++) labels[i] = dilated[i] ? -1 : 0;
    const panels = [null]; // index 0 unused; panels[id] = {minX, maxX, minY, maxY, horiz}
    let nextId = 1;
    const queue = new Int32Array(N);

    for (let y = 0; y < ch; y++) {
      for (let x = 0; x < cw; x++) {
        const idx = y * cw + x;
        if (labels[idx] !== 0) continue;
        const id = nextId++;
        labels[idx] = id;
        let minX = x, maxX = x, minY = y, maxY = y;
        let head = 0, tail = 0;
        queue[tail++] = idx;
        while (head < tail) {
          const ci = queue[head++];
          const cx = ci % cw, cy = (ci / cw) | 0;
          if (cx < minX) minX = cx; if (cx > maxX) maxX = cx;
          if (cy < minY) minY = cy; if (cy > maxY) maxY = cy;
          if (cx > 0     && labels[ci - 1]  === 0) { labels[ci - 1]  = id; queue[tail++] = ci - 1; }
          if (cx < cw - 1 && labels[ci + 1]  === 0) { labels[ci + 1]  = id; queue[tail++] = ci + 1; }
          if (cy > 0     && labels[ci - cw] === 0) { labels[ci - cw] = id; queue[tail++] = ci - cw; }
          if (cy < ch - 1 && labels[ci + cw] === 0) { labels[ci + cw] = id; queue[tail++] = ci + cw; }
        }
        const pw = maxX - minX, ph = maxY - minY;
        panels[id] = { minX, maxX, minY, maxY, horiz: pw > ph };
      }
    }

    // Build per-pixel gloss overlay — streak runs along the panel's long axis
    const glossC = document.createElement('canvas');
    glossC.width = cw; glossC.height = ch;
    const gctx = glossC.getContext('2d');
    const glossImg = gctx.createImageData(cw, ch);
    const gd = glossImg.data;

    for (let i = 0; i < N; i++) {
      const pid = labels[i];
      if (pid <= 0) continue;
      const panel = panels[pid];
      // Use long axis for gradient direction
      const span = panel.horiz ? (panel.maxX - panel.minX) : (panel.maxY - panel.minY);
      if (span < 3) continue;
      const cx = i % cw, cy = (i / cw) | 0;
      // Cross-axis: perpendicular to long axis — this is where the streak sits
      const t = panel.horiz
        ? (cy - panel.minY) / (panel.maxY - panel.minY || 1)
        : (cx - panel.minX) / (panel.maxX - panel.minX || 1);
      let r, g, b, a;
      if (t < 0.12) {
        r = g = b = 255; a = 8;
      } else if (t < 0.20) {
        r = g = b = 255; a = 8 + ((t - 0.12) / 0.08 * 48) | 0;
      } else if (t < 0.26) {
        r = g = b = 255; a = (56 * (1 - (t - 0.20) / 0.06)) | 0;
      } else if (t < 0.70) {
        r = g = b = a = 0;
      } else {
        r = g = b = 0; a = (((t - 0.70) / 0.30) * 22) | 0;
      }
      gd[i * 4] = r; gd[i * 4 + 1] = g; gd[i * 4 + 2] = b; gd[i * 4 + 3] = a;
    }
    gctx.putImageData(glossImg, 0, 0);
    entry._glossCanvas = glossC;
    return glossC;
  }

  /**
   * Build a sparkle overlay — random bright white dots scattered across the surface.
   * Used for metallic sparkle finishes (Platinum Sparkle, Ebony Chrome, Sun Gold).
   * Cached per entry, clipped to silhouette.
   */
  _buildSparkleCanvas(entry) {
    if (entry._sparkleCanvas) return entry._sparkleCanvas;
    const { cw, ch } = entry;
    if (!cw || !ch) return null;
    const sil = entry.silCanvas || this._ensureEquipmentCanvas(entry, 'sil');
    if (!sil) return null;
    const tmp = document.createElement('canvas');
    tmp.width = cw; tmp.height = ch;
    const tctx = tmp.getContext('2d');
    tctx.drawImage(sil, 0, 0);
    const silData = tctx.getImageData(0, 0, cw, ch).data;
    const spkC = document.createElement('canvas');
    spkC.width = cw; spkC.height = ch;
    const sctx = spkC.getContext('2d');
    const spkImg = sctx.createImageData(cw, ch);
    const sd = spkImg.data;
    // Deterministic pseudo-random for consistency across renders
    let seed = (cw * 7 + ch * 13) | 0;
    const rand = () => { seed = (seed * 1664525 + 1013904223) & 0x7fffffff; return seed / 0x7fffffff; };
    // Density: ~1 sparkle per 50 sq px on average
    const density = 1 / 50;
    const N = cw * ch;
    for (let i = 0; i < N; i++) {
      if (silData[i * 4 + 3] < 30) continue; // outside silhouette
      if (rand() > density) continue;
      // Bright white dot with varying intensity
      const brightness = 200 + (rand() * 55) | 0;
      const alpha = 90 + (rand() * 140) | 0;
      sd[i * 4] = brightness;
      sd[i * 4 + 1] = brightness;
      sd[i * 4 + 2] = brightness;
      sd[i * 4 + 3] = alpha;
    }
    sctx.putImageData(spkImg, 0, 0);
    entry._sparkleCanvas = spkC;
    return spkC;
  }

  /**
   * Build a subtle grain/noise overlay for textured/matte powder coats.
   * Random speckle pattern clipped to silhouette, cached per entry.
   */
  _buildGrainCanvas(entry) {
    if (entry._grainCanvas) return entry._grainCanvas;
    const { cw, ch } = entry;
    if (!cw || !ch) return null;
    const sil = entry.silCanvas || this._ensureEquipmentCanvas(entry, 'sil');
    if (!sil) return null;
    const silData = document.createElement('canvas');
    silData.width = cw; silData.height = ch;
    silData.getContext('2d').drawImage(sil, 0, 0);
    const sd = silData.getContext('2d').getImageData(0, 0, cw, ch).data;
    // Generate block-based noise at ~4px cell size
    const cellSize = 2;
    const cols = Math.ceil(cw / cellSize);
    const rows = Math.ceil(ch / cellSize);
    // Pre-compute random values per cell
    const cellVal = new Uint8Array(cols * rows);
    const cellAlpha = new Uint8Array(cols * rows);
    for (let i = 0; i < cols * rows; i++) {
      cellVal[i] = Math.random() > 0.5 ? 255 : 0;
      cellAlpha[i] = (Math.random() * 22) | 0;
    }
    const gc = document.createElement('canvas');
    gc.width = cw; gc.height = ch;
    const gctx = gc.getContext('2d');
    const img = gctx.createImageData(cw, ch);
    const d = img.data;
    for (let y = 0; y < ch; y++) {
      const row = (y / cellSize) | 0;
      for (let x = 0; x < cw; x++) {
        const i = y * cw + x;
        if (sd[i * 4 + 3] < 30) continue;
        const col = (x / cellSize) | 0;
        const ci = row * cols + col;
        const v = cellVal[ci];
        d[i * 4]     = v;
        d[i * 4 + 1] = v;
        d[i * 4 + 2] = v;
        d[i * 4 + 3] = cellAlpha[ci];
      }
    }
    gctx.putImageData(img, 0, 0);
    entry._grainCanvas = gc;
    return gc;
  }

  _getColoredCanvas(source, color, entry, cacheProp, colorProp, glossy, matte, sparkle) {
    const key = sparkle ? color + ':s' : glossy ? color + ':g' : matte ? color + ':m' : color;
    if (entry[cacheProp] && entry[colorProp] === key) return entry[cacheProp];
    const { width, height } = source;
    let c = entry[cacheProp];
    if (!c || c.width !== width || c.height !== height) {
      c = document.createElement('canvas');
      c.width = width; c.height = height;
    }
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(source, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, width, height);
    // Per-panel glossy sheen: each enclosed steel panel gets its own gradient
    if (glossy || sparkle) {
      const gloss = this._buildGlossCanvas(entry);
      if (gloss) {
        ctx.globalCompositeOperation = 'source-atop';
        ctx.drawImage(gloss, 0, 0);
        // Sparkle: double the gloss for extra brightness + add sparkle dots
        if (sparkle) {
          ctx.drawImage(gloss, 0, 0);
          const spk = this._buildSparkleCanvas(entry);
          if (spk) ctx.drawImage(spk, 0, 0);
        }
      }
    }
    // Subtle grain texture for matte/textured powder coats
    if (matte) {
      const grain = this._buildGrainCanvas(entry);
      if (grain) {
        ctx.globalCompositeOperation = 'source-atop';
        ctx.drawImage(grain, 0, 0);
      }
    }
    ctx.globalCompositeOperation = 'source-over';
    entry[cacheProp] = c;
    entry[colorProp] = key;
    return c;
  }

  /**
   * Ensure a pattern image (camo swatch) is loaded and cached.
   * Called when user selects a camo upholstery or on design load.
   * @param {string} swatchKey - swatch key, e.g. 'uph-woodland-camo'
   */
  _ensurePatternImage(swatchKey) {
    if (!this._patternImages) this._patternImages = {};
    if (this._patternImages[swatchKey]) return; // already loaded or loading
    // Prefer larger dedicated pattern image; fall back to swatch thumbnail
    const url = (window.__rdPatternUrls && window.__rdPatternUrls[swatchKey])
      || (window.__rdSwatchUrls && window.__rdSwatchUrls[swatchKey]);
    if (!url) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    this._patternImages[swatchKey] = { img, loaded: false };
    img.onload = () => {
      this._patternImages[swatchKey].loaded = true;
      // Coalesce pattern loads into one render
      clearTimeout(this._equipRenderTimer);
      this._equipRenderTimer = setTimeout(() => { this._equipRenderTimer = null; this.render(); }, 200);
    };
    img.src = url;
  }

  /**
   * Get the pattern source for a given orientation.
   * For landscape pads with a portrait swatch, returns a 90°-rotated canvas.
   * Otherwise returns the original image. Cached on the pattern entry.
   */
  _getPatternSource(patternEntry, landscape) {
    const img = patternEntry.img;
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    // Rotate only if pad is landscape and swatch is portrait
    if (landscape && ih > iw) {
      if (!patternEntry._rotSrc) {
        const rc = document.createElement('canvas');
        rc.width = ih; rc.height = iw;
        const rctx = rc.getContext('2d');
        rctx.translate(ih, 0);
        rctx.rotate(Math.PI / 2);
        rctx.drawImage(img, 0, 0);
        patternEntry._rotSrc = rc;
      }
      return patternEntry._rotSrc;
    }
    return img;
  }

  /**
   * Get an upholstery canvas filled with a pattern image.
   * The pattern origin is offset to the pad bounding box top-left so the
   * swatch always starts fresh at the pad edge. With large source images
   * (e.g. 300x1000) the pattern covers most pads without any visible repeat.
   * Detects pad orientation and rotates the swatch for landscape pads.
   * @param {HTMLCanvasElement} source - upholstery alpha mask
   * @param {HTMLImageElement} patternImg - loaded swatch image
   * @param {string} cacheKey - swatch key for cache lookup and invalidation
   * @param {Object} entry - equipment entry for caching
   * @returns {HTMLCanvasElement}
   */
  _getPatternedCanvas(source, patternImg, cacheKey, entry) {
    if (entry._patternedUphCanvas && entry._patternedUphCacheKey === cacheKey) return entry._patternedUphCanvas;
    const { width, height } = source;
    let c = entry._patternedUphCanvas;
    if (!c || c.width !== width || c.height !== height) {
      c = document.createElement('canvas');
      c.width = width; c.height = height;
    }
    // Compute pad bounding box from upholstery mask (cached)
    if (entry._padBBox == null) {
      const data = source.getContext('2d').getImageData(0, 0, width, height).data;
      let minX = width, maxX = 0, minY = height, maxY = 0;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] > 10) {
          const idx = (i - 3) / 4;
          const px = idx % width, py = (idx / width) | 0;
          if (px < minX) minX = px;
          if (px > maxX) maxX = px;
          if (py < minY) minY = py;
          if (py > maxY) maxY = py;
        }
      }
      entry._padBBox = { minX, minY, maxX, maxY };
      entry._padLandscape = (maxX - minX) > (maxY - minY);
    }
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(source, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    // Use raw image (or rotated for landscape) — no mirror tiling.
    // Offset pattern origin to pad top-left so the swatch starts at the pad edge.
    const patEntry = this._patternImages && this._patternImages[cacheKey];
    const src = patEntry ? this._getPatternSource(patEntry, entry._padLandscape) : patternImg;
    const pat = ctx.createPattern(src, 'repeat');
    const bb = entry._padBBox;
    ctx.translate(bb.minX, bb.minY);
    ctx.fillStyle = pat;
    ctx.fillRect(-bb.minX, -bb.minY, width, height);
    ctx.globalCompositeOperation = 'source-over';
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    entry._patternedUphCanvas = c;
    entry._patternedUphCacheKey = cacheKey;
    return c;
  }

  /**
   * Get a silhouette canvas filled with a tiled pattern image (e.g. Silver Vein).
   * The pattern is scaled to a consistent world-space density so the texture
   * looks the same size on every piece of equipment regardless of SVG resolution.
   * @param {HTMLCanvasElement} silCanvas - silhouette bitmap (mask)
   * @param {HTMLImageElement} patternImg - loaded pattern image
   * @param {string} cacheKey - pattern key for cache lookup
   * @param {Object} entry - equipment entry for caching
   * @param {number} objWidthFeet - equipment width in feet (for world-space scaling)
   */
  _getPatternedPcCanvas(silCanvas, patternImg, cacheKey, entry, objWidthFeet) {
    if (entry._patternedPcCanvas && entry._patternedPcCacheKey === cacheKey) return entry._patternedPcCanvas;
    const { width, height } = silCanvas;
    let c = entry._patternedPcCanvas;
    if (!c || c.width !== width || c.height !== height) {
      c = document.createElement('canvas');
      c.width = width; c.height = height;
    }
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(silCanvas, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    const patEntry = this._patternImages && this._patternImages[cacheKey];
    const src = patEntry ? patEntry.img : patternImg;
    // Scale pattern to consistent world-space size: one tile covers TARGET_FEET
    const TARGET_FEET = 1.5;
    const ppf = objWidthFeet > 0 ? width / objWidthFeet : width;
    const tileSize = ppf * TARGET_FEET;
    const scale = tileSize / src.naturalWidth;
    const pat = ctx.createPattern(src, 'repeat');
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.fillStyle = pat;
    ctx.fillRect(0, 0, width / scale, height / scale);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    entry._patternedPcCanvas = c;
    entry._patternedPcCacheKey = cacheKey;
    return c;
  }

  /**
   * Build edge highlight canvas for upholstery pads.
   * BFS inward from pad edges produces a white glow that fades toward the
   * center, giving pads a cushioned/rounded appearance. Cached per entry.
   */
  _buildUphEdgeHighlight(entry) {
    if (entry._uphEdgeHighlight) return entry._uphEdgeHighlight;
    if (!entry.upholsteryCanvas) return null;
    const { cw, ch } = entry;
    if (!cw || !ch) return null;
    // Read upholstery mask
    const tmp = document.createElement('canvas');
    tmp.width = cw; tmp.height = ch;
    const tctx = tmp.getContext('2d');
    tctx.drawImage(entry.upholsteryCanvas, 0, 0);
    const maskData = tctx.getImageData(0, 0, cw, ch).data;
    const N = cw * ch;
    // Build binary pad mask (1 = pad pixel)
    const pad = new Uint8Array(N);
    for (let i = 0; i < N; i++) pad[i] = maskData[i * 4 + 3] > 20 ? 1 : 0;
    // BFS from boundary pixels inward — distance field
    const dist = new Float32Array(N);
    dist.fill(-1);
    const queue = new Int32Array(N);
    let head = 0, tail = 0;
    // Seed: pad pixels adjacent to non-pad (edge pixels)
    for (let y = 0; y < ch; y++) {
      for (let x = 0; x < cw; x++) {
        const i = y * cw + x;
        if (!pad[i]) continue;
        const isEdge = x === 0 || x === cw - 1 || y === 0 || y === ch - 1
          || !pad[i - 1] || !pad[i + 1] || !pad[i - cw] || !pad[i + cw];
        if (isEdge) { dist[i] = 0; queue[tail++] = i; }
      }
    }
    // BFS
    while (head < tail) {
      const i = queue[head++];
      const d = dist[i] + 1;
      const x = i % cw, y = (i - x) / cw;
      const nb = [];
      if (x > 0) nb.push(i - 1);
      if (x < cw - 1) nb.push(i + 1);
      if (y > 0) nb.push(i - cw);
      if (y < ch - 1) nb.push(i + cw);
      for (const ni of nb) {
        if (pad[ni] && dist[ni] < 0) { dist[ni] = d; queue[tail++] = ni; }
      }
    }
    // Highlight depth scales with equipment bitmap size
    const maxDepth = Math.max(5, Math.min(cw, ch) * 0.025);
    const peakAlpha = 30;
    // Build highlight image
    const hc = document.createElement('canvas');
    hc.width = cw; hc.height = ch;
    const hctx = hc.getContext('2d');
    const img = hctx.createImageData(cw, ch);
    const d = img.data;
    for (let i = 0; i < N; i++) {
      if (dist[i] < 0) continue;
      const t = dist[i] / maxDepth; // 0 = edge, 1 = deep inside
      if (t >= 1) continue;
      const a = peakAlpha * (1 - t) * (1 - t); // quadratic falloff
      d[i * 4] = 255;
      d[i * 4 + 1] = 255;
      d[i * 4 + 2] = 255;
      d[i * 4 + 3] = a | 0;
    }
    hctx.putImageData(img, 0, 0);
    entry._uphEdgeHighlight = hc;
    return hc;
  }

  /**
   * Load texture overlay images (crinkle leather, checker plate, etc.)
   * from Liquid-provided URLs. Called once during init.
   */
  _loadTextureOverlays() {
    if (!this._textureOverlays) this._textureOverlays = {};
    const urls = window.__rdTextureOverlays;
    if (!urls) return;
    for (const key of Object.keys(urls)) {
      if (this._textureOverlays[key]) continue;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      this._textureOverlays[key] = { img, loaded: false };
      img.onload = () => {
        this._textureOverlays[key].loaded = true;
        // Coalesce texture overlay loads into one render
        clearTimeout(this._equipRenderTimer);
        this._equipRenderTimer = setTimeout(() => { this._equipRenderTimer = null; this.render(); }, 200);
      };
      img.src = urls[key];
    }
  }

  /**
   * Build crinkle leather texture canvas clipped to upholstery mask.
   * Tiles the crinkle overlay image over the pad regions, cached per entry.
   * Returns null if crinkle texture isn't loaded or entry has no upholstery.
   */
  _buildCrinkleUphCanvas(entry) {
    if (entry._crinkleUphCanvas) return entry._crinkleUphCanvas;
    const tex = this._textureOverlays && this._textureOverlays.crinkle;
    if (!tex || !tex.loaded) return null;
    if (!entry.upholsteryCanvas) return null;
    const { cw, ch } = entry;
    if (!cw || !ch) return null;
    const c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    const ctx = c.getContext('2d');
    // Tile the crinkle texture at 50% opacity so pad color shows through
    const pat = ctx.createPattern(tex.img, 'repeat');
    ctx.globalAlpha = 0.25;
    ctx.fillStyle = pat;
    ctx.fillRect(0, 0, cw, ch);
    ctx.globalAlpha = 1;
    // Clip to upholstery mask via source-in
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(entry.upholsteryCanvas, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    entry._crinkleUphCanvas = c;
    return c;
  }

  /**
   * Build checker plate texture canvas clipped to chrome mask.
   * Tiles the diamond plate overlay image over chrome/silver regions.
   * Cached per entry.
   */
  _buildCheckerChromeCanvas(entry) {
    if (entry._checkerChromeCanvas) return entry._checkerChromeCanvas;
    const tex = this._textureOverlays && this._textureOverlays.checkerplate;
    if (!tex || !tex.loaded) return null;
    if (!entry.chromeCanvas) return null;
    const { cw, ch } = entry;
    if (!cw || !ch) return null;
    const c = document.createElement('canvas');
    c.width = cw; c.height = ch;
    const ctx = c.getContext('2d');
    // Scale texture down to half size for denser diamond repeat
    const scale = 0.5;
    const sw = (tex.img.naturalWidth || tex.img.width) * scale | 0;
    const sh = (tex.img.naturalHeight || tex.img.height) * scale | 0;
    const small = document.createElement('canvas');
    small.width = sw; small.height = sh;
    small.getContext('2d').drawImage(tex.img, 0, 0, sw, sh);
    const pat = ctx.createPattern(small, 'repeat');
    ctx.fillStyle = pat;
    ctx.fillRect(0, 0, cw, ch);
    // Clip to chrome mask
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(entry.chromeCanvas, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    entry._checkerChromeCanvas = c;
    return c;
  }

  /**
   * Clear all colored canvas caches for a specific vendor.
   * Called when user changes powder coat or upholstery color for a brand.
   */
  _invalidateBrandColorCaches(vendor) {
    for (const id in this._equipmentImages) {
      const entry = this._equipmentImages[id];
      if (!entry.loaded || entry.vendor !== vendor) continue;
      entry._coloredSilCanvas = null;
      entry._powderCoatCachedColor = null;
      entry._coloredUphCanvas = null;
      entry._upholsteryCachedColor = null;
      entry._patternedUphCanvas = null;
      entry._patternedUphCacheKey = null;
      entry._patternedPcCanvas = null;
      entry._patternedPcCacheKey = null;
    }
    this.render();
  }

  placeEquipment(equipmentId, x, y, rotation) {
    const info = this._equipmentImages[equipmentId];
    if (!info) { console.warn('Unknown equipment:', equipmentId); return; }
    const widthFt = info.widthIn / 12;
    const depthFt = info.depthIn / 12;
    const tightWFt = (info.tightWidthIn || info.widthIn) / 12;
    const tightDFt = (info.tightDepthIn || info.depthIn) / 12;
    const tightOX = (info.tightOffsetXIn || 0) / 12;
    const tightOY = (info.tightOffsetYIn || 0) / 12;
    // Default to center of viewport if no coords given
    if (x === undefined || y === undefined) {
      const center = this.screenToWorld(this.canvas.width / (2 * (window.devicePixelRatio || 1)), this.canvas.height / (2 * (window.devicePixelRatio || 1)));
      x = this.snapToGrid(center.x, center.y).x;
      y = this.snapToGrid(center.x, center.y).y;
    }
    this.saveUndoState();
    this.objects.push({
      type: 'equipment',
      equipmentId: equipmentId,
      x: x,
      y: y,
      width: widthFt,
      depth: depthFt,
      tightWidth: tightWFt,
      tightDepth: tightDFt,
      tightOffsetX: tightOX,
      tightOffsetY: tightOY,
      rotation: rotation || 0
    });
    // Save placement info for shift-hold cardinal axis chaining (edge-gap snapping)
    // Use tight (visual) dimensions so edge-gap snapping matches visible content
    this._lastPlacedEquipPos = { x: x, y: y, widthFt: tightWFt, depthFt: tightDFt, rotation: rotation || 0 };
    // Splash effect at placement point
    if (this._animateRectLock) this._animateRectLock({ x: x, y: y }, 'blue');
    this.updateStatus(`Equipment placed: ${info.title}`);
    this.render();
    if (typeof this.updateMeasurements === 'function') this.updateMeasurements();
    // Nudge: after 30s idle on equipment tab, pulse the "Get a Quote" tab once
    this._resetQuoteNudgeTimer();
  }

  _resetQuoteNudgeTimer() {
    clearTimeout(this._quoteNudgeTimer);
    // Remove any active nudge class
    const existing = document.querySelector('.rd-step-tab[data-step="quote"].rd-tab-nudge');
    if (existing) existing.classList.remove('rd-tab-nudge');
    // After 30s idle on equipment tab, breathe orange 3 times then stop
    this._quoteNudgeTimer = setTimeout(() => {
      if (!this._equipmentMode) return;
      const tab = document.querySelector('.rd-step-tab[data-step="quote"]');
      if (!tab || tab.classList.contains('active') || tab.classList.contains('disabled')) return;
      tab.classList.add('rd-tab-nudge');
      tab.addEventListener('animationend', (e) => {
        if (e.target === tab) tab.classList.remove('rd-tab-nudge');
      }, { once: true });
    }, 30000);
  }

  /**
   * Compute cardinal-axis snap with edge-to-edge gap rounding to whole feet.
   * @param {number} rawX - un-snapped world X of cursor
   * @param {number} rawY - un-snapped world Y of cursor
   * @param {number} ghostWidthFt - ghost piece width in feet
   * @param {number} ghostDepthFt - ghost piece depth in feet
   * @param {number} ghostRot - ghost piece rotation in radians
   * @returns {object|null} { x, y, gap, axis, sign, edge1:{x,y}, edge2:{x,y} } or null
   */
  _computeCardinalEquipSnap(rawX, rawY, ghostWidthFt, ghostDepthFt, ghostRot) {
    const lp = this._lastPlacedEquipPos;
    if (!lp) return null;
    const dx0 = rawX - lp.x;
    const dy0 = rawY - lp.y;
    const axis = Math.abs(dx0) >= Math.abs(dy0) ? 'x' : 'y';
    const sign = axis === 'x' ? (dx0 >= 0 ? 1 : -1) : (dy0 >= 0 ? 1 : -1);
    // Find the nearest same-type piece along the axis instead of always using last-placed.
    // This lets the user reverse direction and snap to the closest neighbor.
    let ref = lp;
    const pid = this._pendingEquipmentId;
    if (pid && this.objects) {
      const crossAxis = axis === 'x' ? 'y' : 'x';
      const mPos = axis === 'x' ? rawX : rawY;
      const mCross = crossAxis === 'x' ? rawX : rawY;
      let bestEdgeDist = Infinity;
      for (const obj of this.objects) {
        if (obj.type !== 'equipment' || obj.equipmentId !== pid) continue;
        const tw = obj.tightWidth || obj.width, td = obj.tightDepth || obj.depth;
        const cPos = axis === 'x' ? obj.x : obj.y;
        const cCross = crossAxis === 'x' ? obj.x : obj.y;
        // Skip pieces that aren't roughly aligned on the cross-axis
        const crossHalf = axis === 'x'
          ? (tw / 2) * Math.abs(Math.sin(obj.rotation || 0)) + (td / 2) * Math.abs(Math.cos(obj.rotation || 0))
          : (tw / 2) * Math.abs(Math.cos(obj.rotation || 0)) + (td / 2) * Math.abs(Math.sin(obj.rotation || 0));
        if (Math.abs(mCross - cCross) > crossHalf + 3) continue;
        // Piece must be "behind" the ghost (opposite to movement direction)
        if ((mPos - cPos) * sign <= 0) continue;
        // Compute facing edge distance to mouse
        const cosC = Math.abs(Math.cos(obj.rotation || 0)), sinC = Math.abs(Math.sin(obj.rotation || 0));
        const halfC = axis === 'x' ? (tw / 2) * cosC + (td / 2) * sinC : (tw / 2) * sinC + (td / 2) * cosC;
        const facingEdge = cPos + sign * halfC;
        const edgeDist = Math.abs(mPos - facingEdge);
        if (edgeDist < bestEdgeDist) {
          bestEdgeDist = edgeDist;
          ref = { x: obj.x, y: obj.y, widthFt: tw, depthFt: td, rotation: obj.rotation || 0 };
        }
      }
    }
    // Half-extents along the constrained axis (rotated bounding box projection)
    const cosO = Math.abs(Math.cos(ref.rotation));
    const sinO = Math.abs(Math.sin(ref.rotation));
    const cosG = Math.abs(Math.cos(ghostRot));
    const sinG = Math.abs(Math.sin(ghostRot));
    let halfO, halfG;
    if (axis === 'x') {
      halfO = (ref.widthFt / 2) * cosO + (ref.depthFt / 2) * sinO;
      halfG = (ghostWidthFt / 2) * cosG + (ghostDepthFt / 2) * sinG;
    } else {
      halfO = (ref.widthFt / 2) * sinO + (ref.depthFt / 2) * cosO;
      halfG = (ghostWidthFt / 2) * sinG + (ghostDepthFt / 2) * cosG;
    }
    const dx = rawX - ref.x;
    const dy = rawY - ref.y;
    const rawDist = axis === 'x' ? Math.abs(dx) : Math.abs(dy);
    const rawGap = rawDist - halfO - halfG;
    const snappedGap = Math.max(0, Math.floor(rawGap));
    const finalDist = snappedGap + halfO + halfG;
    const gx = axis === 'x' ? ref.x + sign * finalDist : ref.x;
    const gy = axis === 'y' ? ref.y + sign * finalDist : ref.y;
    // Edge points for measurement line (facing edges of each piece)
    const e1 = axis === 'x'
      ? { x: ref.x + sign * halfO, y: ref.y }
      : { x: ref.x, y: ref.y + sign * halfO };
    const e2 = axis === 'x'
      ? { x: gx - sign * halfG, y: gy }
      : { x: gx, y: gy - sign * halfG };
    return { x: gx, y: gy, gap: snappedGap, axis, sign, edge1: e1, edge2: e2 };
  }
}

window.RoomDesignerEquipment = RoomDesignerEquipment;
